/**
 * React Query hooks for Employees
 * Example of how to use React Query with the API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { employeesApi } from '../lib/api';

// Query keys (for cache management)
export const employeeKeys = {
  all: ['employees'],
  lists: () => [...employeeKeys.all, 'list'],
  list: (filters) => [...employeeKeys.lists(), filters],
  details: () => [...employeeKeys.all, 'detail'],
  detail: (id) => [...employeeKeys.details(), id],
};

// Get all employees
export function useEmployees(active) {
  return useQuery({
    queryKey: employeeKeys.list({ active }),
    queryFn: () => employeesApi.getAll(active),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache even when component unmounts
    networkMode: 'always', // Don't pause queries
  });
}

// Get single employee
export function useEmployee(id) {
  return useQuery({
    queryKey: employeeKeys.detail(id),
    queryFn: () => employeesApi.getById(id),
    enabled: !!id, // Only run if id exists
  });
}

// Create employee mutation
export function useCreateEmployee() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: employeesApi.create,
    onSuccess: () => {
      // Invalidate and refetch employees list
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
    },
  });
}

// Update employee mutation
export function useUpdateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => employeesApi.update(id, data),
    onSuccess: (data, variables) => {
      // Invalidate specific employee and list
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });

      // When employee data changes (like wages), invalidate salary and payment calculations
      // This ensures historical calculations remain accurate
      queryClient.invalidateQueries({ queryKey: ['salaries'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
  });
}

// Delete employee mutation
export function useDeleteEmployee() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: employeesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
    },
  });
}

// Toggle active mutation
export function useToggleEmployeeActive() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: employeesApi.toggleActive,
    onSuccess: (data, employeeId) => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(employeeId) });
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
    },
  });
}

